/**
 * THE NAME ADJUSTER, client side (2026-10-31). Twin file: frontend/src/lib and
 * mobile-app/src/lib must stay byte-identical.
 *
 * Guillaume: "Use name adjuster every single time we must mention an asset
 * because mentioning the same asset back to back with one time qualifying it as
 * Stock and the other time as Crypto is absurd." His tray showed SNDK twice:
 * the Phemex row (SNDKUSDT, a stock) and the KuCoin row (SNDKUSDTM, unknown to
 * the register, so guessed crypto). Every venue spelling is read back to the
 * register's asset here, so one asset has one name and one type everywhere.
 *
 * Order: the spelling itself, then each venue rewrite (KuCoin ...USDTM and XBT,
 * MEXC ..._USDT and ...STOCKUSDT, Phemex u1000..., Hyperliquid XYZ...), each
 * tried as a register key and then as a canonical name. Only when nothing
 * matches does the caller fall back to a name-based guess, on the cleanest
 * spelling found (so "XYZSNDK" still reads "SNDK").
 */

export type NamedInstrument = { symbol: string; canonical: string };

/** Every spelling a venue symbol could be filed under, most literal first. */
export function venueSpellings(symbol: string | null | undefined): string[] {
  const s = String(symbol ?? '').toUpperCase().trim();
  if (!s) return [];
  const out: string[] = [];
  const push = (v: string) => {
    if (v && !out.includes(v)) out.push(v);
  };
  push(s);
  let t = s.replace(/[/_-]USDT$/, 'USDT').replace(/[/_-]USDC$/, 'USDC');
  push(t);
  if (t.endsWith('USDTM')) t = t.slice(0, -1); // KuCoin linear perpetuals
  push(t);
  if (t.startsWith('XBT')) push((t = 'BTC' + t.slice(3))); // KuCoin's bitcoin
  if (/^U\d/.test(t)) push((t = t.slice(1))); // Phemex u1000PEPEUSDT
  if (t.endsWith('STOCKUSDT') && t.length > 9) push((t = t.slice(0, -9) + 'USDT')); // MEXC stocks
  if (t.startsWith('XYZ') && t.length > 3) { // Hyperliquid HIP-3 book (xyz:SNDK)
    t = t.slice(3);
    push(t.endsWith('USDT') ? t : t + 'USDT');
    t = t.endsWith('USDT') ? t : t + 'USDT';
  }
  if (!/(USDT|USDC|USD)$/.test(t)) push(t + 'USDT'); // a bare base
  return out;
}

const canonIndex = new WeakMap<Map<string, NamedInstrument>, Map<string, NamedInstrument>>();

/** The register's entries by canonical name (the first spelling filed under it). */
function byCanonical<T extends NamedInstrument>(bySymbol: Map<string, T>): Map<string, T> {
  let idx = canonIndex.get(bySymbol) as Map<string, T> | undefined;
  if (!idx) {
    idx = new Map<string, T>();
    // A canonical name's own row wins over another venue's spelling of it.
    for (const [k, v] of bySymbol) if (k === v.canonical) idx.set(v.canonical, v);
    for (const v of bySymbol.values()) if (!idx.has(v.canonical)) idx.set(v.canonical, v);
    canonIndex.set(bySymbol, idx);
  }
  return idx;
}

/**
 * The register's entry for any venue spelling, or undefined (and the cleanest
 * spelling, for the caller's fallback). With a venue ("MEXC", "HYPERLIQUID"),
 * that venue's own "SYMBOL@VENUE" entry comes first.
 */
export function resolveSpelling<T extends NamedInstrument>(
  symbol: string | null | undefined,
  bySymbol: Map<string, T> | undefined,
  venue = '',
): { hit?: T; clean: string } {
  const cands = venueSpellings(symbol);
  const clean = cands[cands.length - 1] ?? '';
  if (!bySymbol || cands.length === 0) return { clean };
  if (venue) {
    const own = bySymbol.get(`${cands[0]}@${venue}`);
    if (own) return { hit: own, clean };
  }
  for (const c of cands) {
    const hit = bySymbol.get(c);
    if (hit) return { hit, clean };
  }
  const idx = byCanonical(bySymbol);
  for (const c of cands) {
    const hit = idx.get(c);
    if (hit) return { hit, clean };
  }
  return { clean };
}
